import React from 'react';
import { Box, Typography, FormControl, Select, IconButton, MenuItem } from '@mui/material';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import zeplinColor from 'theme/Color';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  rowsPerPage: number;
  count: number;
  paginatedDataLength: number;
  onFirstPage: () => void;
  onPreviousPage: () => void;
  onNextPage: () => void;
  onLastPage: () => void;
  onRowsPerPageChange: (value: number) => void;
  onPageChange: (page: number) => void;
  pageWindowStart: number;
  setPageWindowStart: (value: number) => void;
  setRowsPerPage: (value: number) => void;
  setCurrentPage: (value: number) => void;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  rowsPerPage,
  count,
  paginatedDataLength,
  onFirstPage,
  onPreviousPage,
  onNextPage,
  onLastPage,
  onRowsPerPageChange,
  onPageChange,
  pageWindowStart,
  setPageWindowStart,
  setCurrentPage,
  setRowsPerPage,
}) => {
  const getVisiblePages = () => {
    const pages: (number | string)[] = [];
    const maxVisiblePages = 4;

    if (totalPages <= maxVisiblePages) {
      for (let i = 0; i < totalPages; i++) {
        pages.push(i);
      }
    } else {
      const windowEnd = Math.min(pageWindowStart + maxVisiblePages, totalPages);

      if (pageWindowStart > 0) {
        pages.push('start-ellipsis');
      }

      for (let i = pageWindowStart; i < windowEnd; i++) {
        pages.push(i);
      }

      if (windowEnd < totalPages) {
        pages.push('end-ellipsis');
      }
    }

    return pages;
  };

  const visiblePages = getVisiblePages();

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '16px',
        backgroundColor: zeplinColor.Neutral1,
        borderRadius: '8px',
        flexShrink: 0,
        marginTop: '16px',
      }}
    >
      {/* Rows Per Page */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
          Rows per page:
        </Typography>
        <FormControl size='small' sx={{ minWidth: 80 }}>
          <Select
            value={rowsPerPage}
            onChange={(e) => {
              const newRows = e.target.value as number;
              setRowsPerPage(newRows);
              setCurrentPage(0);
            }}
            IconComponent={KeyboardArrowDownIcon}
            sx={{
              backgroundColor: zeplinColor.Neutral1,
              border: `1px solid ${zeplinColor.Neutral5}`,
              borderRadius: '6px',
              '& .MuiSelect-select': {
                padding: '6px 8px',
              },
              '& .MuiSelect-icon': {
                color: zeplinColor.BasicBlack,
              },
            }}
          >
            <MenuItem value={5}>5</MenuItem>
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={15}>15</MenuItem>
            <MenuItem value={20}>20</MenuItem>
            <MenuItem value={50}>50</MenuItem>
          </Select>
        </FormControl>
        <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
          1-{Math.min(rowsPerPage, paginatedDataLength)} of {count} rows
        </Typography>
      </Box>

      {/* Page Selection */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <IconButton
          onClick={onFirstPage}
          disabled={currentPage === 0}
          sx={{
            padding: '8px 12px',
            border: `1px solid ${
              currentPage === 0 ? zeplinColor.Neutral5 : zeplinColor.BasicBlack
            }`,
            borderRadius: '8px',
            backgroundColor: currentPage === 0 ? zeplinColor.Neutral3 : zeplinColor.BasicBlack,
            '&.Mui-disabled': {
              backgroundColor: currentPage === 0 ? zeplinColor.Neutral3 : zeplinColor.BasicBlack,
            },
            '&:hover': {
              backgroundColor: currentPage === 0 ? zeplinColor.Neutral3 : zeplinColor.BasicBlack,
            },
          }}
          title='First Page'
        >
          <KeyboardDoubleArrowLeftIcon
            sx={{
              fontSize: 18,
              color: currentPage === 0 ? zeplinColor.Neutral9 : zeplinColor.Neutral1,
            }}
          />
        </IconButton>

        <IconButton
          onClick={onPreviousPage}
          disabled={currentPage === 0}
          sx={{
            padding: '8px 12px',
            border: `1px solid ${
              currentPage === 0 ? zeplinColor.Neutral5 : zeplinColor.BasicBlack
            }`,
            borderRadius: '8px',
            backgroundColor: currentPage === 0 ? zeplinColor.Neutral3 : zeplinColor.BasicBlack,
            '&.Mui-disabled': {
              backgroundColor: currentPage === 0 ? zeplinColor.Neutral3 : zeplinColor.BasicBlack,
            },
            '&:hover': {
              backgroundColor: currentPage === 0 ? zeplinColor.Neutral3 : zeplinColor.BasicBlack,
            },
          }}
          title='Previous Page'
        >
          <KeyboardArrowLeftIcon
            sx={{
              fontSize: 18,
              color: currentPage === 0 ? zeplinColor.Neutral9 : zeplinColor.Neutral1,
            }}
          />
        </IconButton>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          {visiblePages.map((page, index) => {
            if (page === 'start-ellipsis' || page === 'end-ellipsis') {
              return (
                <Typography key={`${page}-${index}`} variant='body2' color={zeplinColor.BasicBlack}>
                  ...
                </Typography>
              );
            }

            return (
              <IconButton
                key={page}
                onClick={() => {
                  const pageNum = page as number;
                  onPageChange(pageNum);
                  setPageWindowStart(Math.max(0, pageNum - 2));
                }}
                sx={{
                  padding: '4px 8px',
                  minWidth: '32px',
                  height: '32px',
                  border: `1px solid ${zeplinColor.Neutral5}`,
                  borderRadius: '6px',
                  backgroundColor:
                    currentPage === page ? zeplinColor.BasicBlack : zeplinColor.Neutral1,
                  '&:hover': {
                    backgroundColor:
                      currentPage === page ? zeplinColor.BasicBlack : zeplinColor.Neutral1,
                  },
                }}
              >
                <Typography
                  variant='body3'
                  color={currentPage === page ? zeplinColor.Neutral1 : zeplinColor.BasicBlack}
                >
                  {Number(page) + 1}
                </Typography>
              </IconButton>
            );
          })}
        </Box>

        <IconButton
          onClick={onNextPage}
          disabled={currentPage >= totalPages - 1}
          sx={{
            padding: '8px 12px',
            border: `1px solid ${
              currentPage >= totalPages - 1 ? zeplinColor.Neutral5 : zeplinColor.BasicBlack
            }`,
            borderRadius: '8px',
            backgroundColor:
              currentPage >= totalPages - 1 ? zeplinColor.Neutral3 : zeplinColor.BasicBlack,
            '&.Mui-disabled': {
              backgroundColor:
                currentPage >= totalPages - 1 ? zeplinColor.Neutral3 : zeplinColor.BasicBlack,
            },
            '&:hover': {
              backgroundColor:
                currentPage >= totalPages - 1 ? zeplinColor.Neutral3 : zeplinColor.BasicBlack,
            },
          }}
          title='Next Page'
        >
          <KeyboardArrowRightIcon
            sx={{
              fontSize: 18,
              color: currentPage >= totalPages - 1 ? zeplinColor.Neutral9 : zeplinColor.Neutral1,
            }}
          />
        </IconButton>

        <IconButton
          onClick={onLastPage}
          disabled={currentPage >= totalPages - 1}
          sx={{
            padding: '8px 12px',
            border: `1px solid ${
              currentPage >= totalPages - 1 ? zeplinColor.Neutral5 : zeplinColor.BasicBlack
            }`,
            borderRadius: '8px',
            backgroundColor:
              currentPage >= totalPages - 1 ? zeplinColor.Neutral3 : zeplinColor.BasicBlack,
            '&.Mui-disabled': {
              backgroundColor:
                currentPage >= totalPages - 1 ? zeplinColor.Neutral3 : zeplinColor.BasicBlack,
            },
            '&:hover': {
              backgroundColor:
                currentPage >= totalPages - 1 ? zeplinColor.Neutral3 : zeplinColor.BasicBlack,
            },
          }}
          title='Last Page'
        >
          <KeyboardDoubleArrowRightIcon
            sx={{
              fontSize: 18,
              color: currentPage >= totalPages - 1 ? zeplinColor.Neutral9 : zeplinColor.Neutral1,
            }}
          />
        </IconButton>
      </Box>
    </Box>
  );
};

export default Pagination;
